import { formatNumber } from '@/shared/lib';

import type { PropertyCandidateResource, PropertyLookupEnvelope, PropertyResource } from './types';

/**
 * Het object. Bouwjaar, type en oppervlak komen bij voorkeur uit de BAG; ze
 * sturen de zichtbaarheid van vragen in de opname, dus ze horen bij het object
 * en niet bij de opdracht.
 */
export interface Property {
    uuid: string;
    street: string;
    houseNumber: string;
    houseNumberSuffix: string | null;
    postcode: string;
    city: string;
    /** "Saffierlaan 14, Middelburg" - zo staat het object in lijsten. */
    shortAddress: string;
    /** "Saffierlaan 14, 4337 KB Middelburg" */
    fullAddress: string;
    buildYear: number | null;
    floorArea: number | null;
    propertyType: string | null;
    propertyTypeLabel: string | null;
}

function houseNumberOf(number: string, suffix: string | null): string {
    return suffix ? `${number}${/^\d/.test(suffix) ? '-' : ''}${suffix}` : number;
}

export function toProperty(resource: PropertyResource): Property {
    const number = houseNumberOf(resource.house_number, resource.house_number_suffix);

    return {
        uuid: resource.uuid,
        street: resource.street,
        houseNumber: resource.house_number,
        houseNumberSuffix: resource.house_number_suffix,
        postcode: resource.postcode,
        city: resource.city,
        shortAddress: `${resource.street} ${number}, ${resource.city}`,
        fullAddress: resource.full_address,
        buildYear: resource.build_year,
        floorArea: resource.floor_area,
        propertyType: resource.property_type,
        propertyTypeLabel: resource.property_type_label,
    };
}

/** "118 m²", "64.446 m²" - Nederlandse notatie, met een echte m². */
export function formatFloorArea(area: number | null): string {
    return area === null ? '—' : `${formatNumber(area)} m²`;
}

/** Postcodes vergelijken zonder spaties en hoofdletters: `4337kb` is `4337 KB`. */
export function normalizePostcode(postcode: string): string {
    return postcode.replace(/\s+/g, '').toUpperCase();
}

/**
 * Kan dit adres worden opgezocht? Een Nederlandse postcode en een huisnummer dat
 * met cijfers begint (`12`, of `12a` met de toevoeging erachter).
 */
export function isSearchableAddress(postcode: string, houseNumber: string): boolean {
    return (
        /^[1-9]\d{3}[A-Z]{2}$/.test(normalizePostcode(postcode)) && /^\d+/.test(houseNumber.trim())
    );
}

/**
 * `12a` in het huisnummerveld: de cijfers zijn het nummer, de rest de
 * toevoeging - tenzij die al apart is ingevuld. Zo leest de API het ook.
 */
export function splitHouseNumber(
    houseNumber: string,
    suffix: string,
): { houseNumber: string; suffix: string | null } {
    const typed = houseNumber.trim();
    const match = /^(\d+)\s*-?\s*([A-Za-z0-9].*)?$/.exec(typed);
    const ownSuffix = suffix.trim();

    if (!match?.[1]) {
        return { houseNumber: typed, suffix: ownSuffix || null };
    }

    return { houseNumber: match[1], suffix: ownSuffix || match[2]?.trim() || null };
}

/** Eén adres uit de BAG, of het object dat dit bedrijf er al heeft. */
export interface PropertyCandidate {
    /** Het object dat dit bedrijf al op dit adres heeft: de keuring komt daarbij. */
    propertyUuid: string | null;
    known: boolean;
    postcode: string;
    street: string | null;
    houseNumber: string;
    /** Zoals de BAG hem schrijft: `T` (huisletter), `-BD` (toevoeging), `A-03`. */
    houseNumberSuffix: string | null;
    /** `1T`, `1-BD`, `93A-03`: het nummer zoals op de deur. */
    houseNumberLabel: string;
    city: string | null;
    fullAddress: string | null;
    bagId: string | null;
    bagObjectId: string | null;
    bagBuildingIds: string[];
    buildYear: number | null;
    floorArea: number | null;
    usagePurposes: string[];
    usagePurposeLabels: string[];
    bagStatus: string | null;
    propertyType: string | null;
    propertyTypeLabel: string | null;
    latitude: number | null;
    longitude: number | null;
}

/** Het antwoord van het adres opzoeken. */
export interface PropertyLookup {
    candidates: PropertyCandidate[];
    found: boolean;
    /** `false`: de BAG antwoordde niet, of de gegevens van het pand ontbreken. */
    complete: boolean;
    /** `false`: de gevraagde toevoeging bestaat niet; `candidates` zijn alle adressen op het nummer. */
    suffixMatched: boolean;
}

function toPropertyCandidate(resource: PropertyCandidateResource): PropertyCandidate {
    return {
        propertyUuid: resource.property_uuid,
        known: resource.source === 'known_property',
        postcode: resource.postcode,
        street: resource.street,
        houseNumber: resource.house_number,
        houseNumberSuffix: resource.house_number_suffix,
        houseNumberLabel: resource.house_number_label,
        city: resource.city,
        fullAddress: resource.full_address,
        bagId: resource.bag_id,
        bagObjectId: resource.bag_object_id,
        bagBuildingIds: resource.bag_building_ids,
        buildYear: resource.build_year,
        floorArea: resource.floor_area,
        usagePurposes: resource.usage_purposes,
        usagePurposeLabels: resource.usage_purpose_labels,
        bagStatus: resource.bag_status,
        propertyType: resource.property_type,
        propertyTypeLabel: resource.property_type_label,
        latitude: resource.latitude,
        longitude: resource.longitude,
    };
}

export function toPropertyLookup(envelope: PropertyLookupEnvelope): PropertyLookup {
    return {
        candidates: envelope.data.map(toPropertyCandidate),
        found: envelope.meta.found,
        complete: envelope.meta.complete,
        suffixMatched: envelope.meta.suffix_matched,
    };
}

/** Wat een adres in een lijst uniek maakt: het BAG-nummer, anders het eigen object. */
export function candidateKey(candidate: PropertyCandidate): string {
    return candidate.bagId ?? candidate.propertyUuid ?? candidate.houseNumberLabel;
}

/** Wat `POST /properties` nodig heeft. */
export interface NewProperty {
    postcode: string;
    street: string;
    houseNumber: string;
    houseNumberSuffix: string | null;
    city: string;
    bagId?: string | null;
    bagObjectId?: string | null;
    bagBuildingIds?: string[];
    buildYear?: number | null;
    floorArea?: number | null;
    propertyType?: string | null;
    usagePurposes?: string[];
    bagStatus?: string | null;
    latitude?: number | null;
    longitude?: number | null;
}

/** Een gekozen adres als nieuw object, met alles wat de BAG erover weet. */
export function candidateToNewProperty(candidate: PropertyCandidate): NewProperty {
    return {
        postcode: candidate.postcode,
        street: candidate.street ?? '',
        houseNumber: candidate.houseNumber,
        houseNumberSuffix: candidate.houseNumberSuffix,
        city: candidate.city ?? '',
        bagId: candidate.bagId,
        bagObjectId: candidate.bagObjectId,
        bagBuildingIds: candidate.bagBuildingIds,
        buildYear: candidate.buildYear,
        floorArea: candidate.floorArea,
        propertyType: candidate.propertyType,
        usagePurposes: candidate.usagePurposes,
        bagStatus: candidate.bagStatus,
        latitude: candidate.latitude,
        longitude: candidate.longitude,
    };
}
